/**
 * Safe localStorage wrapper that handles SecurityError when localStorage is blocked
 * (e.g., in Private/Incognito mode, third-party cookie restrictions, iframes)
 */

export const I18N_STORAGE_KEY = 'i18nextLng';

class SafeStorage {
  private isAvailable: boolean | null = null;

  private checkAvailability(): boolean {
    if (this.isAvailable !== null) {
      return this.isAvailable;
    }

    if (typeof window === 'undefined') {
      this.isAvailable = false;
      return false;
    }

    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      this.isAvailable = true;
      return true;
    } catch (e) {
      this.isAvailable = false;
      return false;
    }
  }

  getItem(key: string): string | null {
    if (!this.checkAvailability()) {
      return null;
    }

    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn(`SafeStorage: Failed to get item "${key}"`, e);
      return null;
    }
  }

  setItem(key: string, value: string): boolean {
    if (!this.checkAvailability()) {
      return false;
    }

    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn(`SafeStorage: Failed to set item "${key}"`, e);
      return false;
    }
  }

  removeItem(key: string): boolean {
    if (!this.checkAvailability()) {
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.warn(`SafeStorage: Failed to remove item "${key}"`, e);
      return false;
    }
  }
}

export const safeStorage = new SafeStorage();
